import React, { useMemo, useState, useEffect, useRef } from "react";
import "./capacitaciones.css";
import {
  Container, Row, Col, ButtonGroup, Button,
  Card, Badge, Spinner, Alert,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import imgFallback from "../assets/no-found.jpeg";

const API_URL = "https://facach.coop.hn/api";

const MONTHS_ORDER = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

const getCurrentMonthName = () => {
  const name = new Intl.DateTimeFormat("es", { month: "long" }).format(new Date());
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const CapacitacionesComp = () => {
  const [allEvents, setAllEvents]             = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [filter, setFilter]                   = useState(null);

  const currentMonthName = useMemo(() => getCurrentMonthName(), []);
  const monthRefs = useRef({});

  useEffect(() => {
    const fetchTraining = async () => {
      setLoading(true); setError(null);
      try {
        const res  = await fetch(`${API_URL}/training`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.ok) {
          setAllEvents(json.data);
          setAvailableMonths(json.availableMonths);
          const monthNames = json.availableMonths.map(m => m.name);
          const initial = monthNames.includes(currentMonthName)
            ? currentMonthName : monthNames[0] || null;
          setFilter(initial);
        }
      } catch (err) {
        console.error("Error cargando capacitaciones:", err.message);
        setError("No se pudieron cargar las capacitaciones.");
      } finally { setLoading(false); }
    };
    fetchTraining();
  }, []);

  useEffect(() => {
    const ref = monthRefs.current[currentMonthName];
    if (ref?.scrollIntoView)
      ref.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [availableMonths, currentMonthName]);

  const filteredItems = useMemo(
    () => allEvents.filter(e => e.month_name === filter),
    [allEvents, filter]
  );

  const sortedMonths = useMemo(
    () => availableMonths.sort((a, b) =>
      MONTHS_ORDER.indexOf(a.name) - MONTHS_ORDER.indexOf(b.name)
    ),
    [availableMonths]
  );

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Cargando capacitaciones…</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger" className="text-center">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <h2 className="text-center my-4">NUESTRAS PRÓXIMAS CAPACITACIONES</h2>

      {/* Barra de meses */}
      {sortedMonths.length > 0 && (
        <ButtonGroup className="mb-4 d-flex justify-content-center flex-wrap months-bar">
          {sortedMonths.map(({ code, name }) => {
            const isActive = filter === name;
            const isToday  = name === currentMonthName;
            return (
              <Button
                key={code}
                ref={el => (monthRefs.current[name] = el)}
                onClick={() => setFilter(name)}
                className={`month-button ${isActive ? "active-month" : ""} ${isToday ? "today-month" : ""}`}
                aria-selected={isActive}
                aria-current={isActive ? "true" : "false"}
                title={isToday ? "Mes actual" : undefined}
              >
                {name}
                {isToday && (
                  <Badge bg="warning" text="dark" className="ms-2 align-middle today-badge">
                    Hoy
                  </Badge>
                )}
              </Button>
            );
          })}
        </ButtonGroup>
      )}

      {/* Tarjetas */}
      <Row className="cap-spacing g-3">  {/* ← cap-spacing en lugar de tight-spacing */}
        {filteredItems.map(item => (
          <Col key={item.id} xs={12} sm={6} md={6}>
            <Card className="cap-card h-100 d-flex"> {/* ← cap-card */}
              <Card.Img
                variant="top"
                src={item.image_url || imgFallback}
                className="cap-img"  
                onError={e => { e.target.src = imgFallback; }}
              />
              <Card.Body className="d-flex flex-column">
                <Card.Title className="cap-title">{item.title}</Card.Title> {/* ← cap-title */}

                {item.audience && <Card.Text>Dirigido a: {item.audience}</Card.Text>}
                <Card.Text>Modalidad: <span style={{ textTransform: "capitalize" }}>{item.modality}</span></Card.Text>
                <Card.Text>Costo: {item.costo}</Card.Text>
                {item.location_text && <Card.Text>Lugar: {item.location_text}</Card.Text>}
                {item.start_datetime && (
                  <Card.Text className="text-muted small">
                    {new Date(item.start_datetime).toLocaleDateString("es-HN", {
                      day: "numeric", month: "long", year: "numeric",
                    })}
                  </Card.Text>
                )}

                <Button
                  variant="primary"
                  as={item.registration_url && item.registration_url !== "a" ? "a" : Link}
                  href={item.registration_url && item.registration_url !== "a" ? item.registration_url : undefined}
                  to={!item.registration_url || item.registration_url === "a" ? "/contacto_capacitacion" : undefined}
                  target={item.registration_url && item.registration_url !== "a" ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="w-26 mb-2"
                >
                  ¡Inscríbete ahora!
                </Button>

                <Button
                  as="a"
                  href={item.calendar_url && item.calendar_url !== "a"
                    ? item.calendar_url
                    : "https://calendar.app.google/qZWPHEoqFhaQ3Fyp9"}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline-primary"
                  size="sm"
                  className="card-news-btn"
                  style={{ background: "white", color: "#252A58", border: "0px solid #252A58", opacity: "0.90" }}
                >
                  Agregar evento a mi calendario 
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}

        {filteredItems.length === 0 && !loading && (
          <Col xs={12}>
            <div className="text-center text-muted py-4">
              No hay capacitaciones programadas para {filter}.
            </div>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default CapacitacionesComp;